import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useI18n } from '../i18n/i18nContext';
import { useUser } from '../contexts/UserContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { userAPI } from '../services/api';
import { isAuthError } from '../utils/apiErrorHandler';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresRegistration?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresRegistration = true
}) => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const { isLoadingUser } = useUser();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const { t } = useI18n();
  const [userStatus, setUserStatus] = useState<{ 
    loading: boolean; 
    isActive: boolean | null; 
    registrationCompleted: boolean | null;
  }>({
    loading: true,
    isActive: null,
    registrationCompleted: null
  });
  
  // Check if user is active and registration completed - wait for AuthTokenManager to finish
  useEffect(() => {
    let isMounted = true;

    // Added retryCount parameter for the delay logic
    const checkUserStatus = async (retryCount = 0) => {
      // Allow access if impersonating (skip normal auth checks)
      if (isImpersonating && impersonatedUser) {
        setUserStatus({
          loading: false,
          isActive: true,
          registrationCompleted: true
        });
        return;
      }

      if (!isAuthenticated || isLoadingUser) return;

      try {
        const profile = await userAPI.getProfile();
        
        if (!isMounted) return;

        // Success case: Registration is complete OR not required. 
        // We update state immediately to allow access.
        if (profile.data.registrationCompleted || !requiresRegistration) {
          setUserStatus({ 
            loading: false, 
            isActive: profile.data.isActive !== false,
            registrationCompleted: true
          });
          return;
        }

        // Edge Case: Registration appears incomplete.
        // If this is the first check (retryCount < 1), we wait 1s and try again.
        // This absorbs race conditions where the backend might be catching up.
        if (retryCount < 1) {
          setTimeout(() => {
            if (isMounted) checkUserStatus(retryCount + 1);
          }, 1000); // 1000ms delay
          return;
        }

        // If we are here, we have retried and it is STILL incomplete.
        // Now we confirm the incomplete status, which will trigger the redirect.
        setUserStatus({ 
          loading: false, 
          isActive: profile.data.isActive !== false,
          registrationCompleted: false
        });

      } catch (error) {
        console.error('Error checking user status:', error);
        
        if (isAuthError(error)) {
          logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }
        
        if (!isMounted) return;

        // On error, we also retry once to be safe against network blips
        if (retryCount < 1) {
          setTimeout(() => {
            if (isMounted) checkUserStatus(retryCount + 1);
          }, 1000);
          return;
        }

        setUserStatus({ 
          loading: false, 
          isActive: true, // Default to active on error
          registrationCompleted: false // Default to not completed on error
        });
      }
    };
    
    checkUserStatus();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoadingUser, requiresRegistration, logout, isImpersonating, impersonatedUser]);

  if (isLoading || isLoadingUser || userStatus.loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  // Allow access if impersonating, otherwise require Auth0 authentication
  if (!isAuthenticated && !isImpersonating) {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname }
    });
    return null;
  }

  // If authenticated but user is inactive, show message and logout (skip for impersonation)
  if (isAuthenticated && !isImpersonating && userStatus.isActive === false) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">{t.auth?.accountDeactivated || 'Account Deactivated'}</h2>
        <p className="text-gray-600 text-center max-w-md">
          {t.auth?.accountDeactivatedMessage || 'Your account has been deactivated. Please contact support for assistance.'}
        </p>
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          {t.auth?.logout || 'Logout'}
        </button>
      </div>
    );
  }

  // If authenticated but registration not completed, redirect to complete registration (skip for impersonation)
  if (requiresRegistration && !isImpersonating && userStatus.registrationCompleted === false) {
    // Store the current path so we can return after registration
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('registration_return_to', currentPath);
    return <Navigate to="/complete-registration" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;